/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardMergedInSection.tsx
 * FILE_ID: UI-KANBAN-CMP-MERGED-IN-001
 * PURPOSE: Card-tab list of cards absorbed into this keeper (from audit).
 * WHY:     Operators see "+N merged" on the tile but the Card tab had no
 *          list — absorbed cards are ARCHIVED and only lived in Activity.
 *          Read-only from audit_log_json; does not mutate counts/data.
 * FLOW:    card.audit_log_json → keeper merge events → Open via onOpenCardId
 */

import { Merge } from 'lucide-react'

import type { AuditLogEntry, Card } from '../../types'

const KEEPER_MERGE_EVENTS = new Set([
  'merged_thread_into_card',
  'merged_duplicate_card',
])

export interface MergedInRow {
  id: string
  via?: string
  at?: string
}

/** Pure: extract not-yet-unmerged absorbed card ids from keeper audit. */
export function listMergedInFromAudit(
  log: AuditLogEntry[] | null | undefined,
): MergedInRow[] {
  if (!log?.length) return []
  const seen = new Set<string>()
  const rows: MergedInRow[] = []
  for (const entry of log) {
    if (!entry || typeof entry !== 'object') continue
    if (!KEEPER_MERGE_EVENTS.has(String(entry.event || ''))) continue
    if (entry.unmerged === true) continue
    const id =
      typeof entry.merged_card_id === 'string' ? entry.merged_card_id : null
    if (!id || seen.has(id)) continue
    seen.add(id)
    rows.push({
      id,
      via: typeof entry.via === 'string' ? entry.via : undefined,
      at:
        typeof entry.ts === 'string'
          ? entry.ts
          : typeof entry.at === 'string'
            ? entry.at
            : undefined,
    })
  }
  return rows
}

export interface CardMergedInSectionProps {
  card: Card
  /** Deep-link into the archived loser (same seam as Activity merge rows). */
  onOpenCardId?: (id: string) => void | Promise<void>
}

export function CardMergedInSection({
  card,
  onOpenCardId,
}: CardMergedInSectionProps) {
  const rows = listMergedInFromAudit(card.audit_log_json)
  if (rows.length === 0) return null

  return (
    <div
      className="rounded-lg border border-amber-200/80 bg-amber-50/50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/20"
      data-testid="kanban-card-merged-in"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-300">
        <Merge className="h-3.5 w-3.5" aria-hidden />
        Merged in ({rows.length})
      </div>
      <p className="mb-2 text-[11px] leading-snug text-amber-900/80 dark:text-amber-200/70">
        Duplicate card{rows.length === 1 ? '' : 's'} folded into this one
        (archived). Open to inspect or undo from Activity.
      </p>
      <ul className="flex flex-col gap-1">
        {rows.map((row, idx) => (
          <li
            key={row.id}
            className="flex items-center justify-between gap-2 text-[11.5px]"
          >
            <span className="min-w-0 truncate font-mono text-neutral-600 dark:text-zinc-400">
              #{idx + 1}{' '}
              <span className="text-neutral-400 dark:text-zinc-500">
                {row.id.slice(0, 8)}…
              </span>
              {row.via ? (
                <span className="ml-1 text-neutral-400 dark:text-zinc-500">
                  via {row.via}
                </span>
              ) : null}
            </span>
            {onOpenCardId ? (
              <button
                type="button"
                onClick={() => void onOpenCardId(row.id)}
                className="shrink-0 rounded px-1.5 py-0.5 font-medium text-amber-800 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-950/60"
              >
                Open
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
